import { useEffect } from "react";
import { useVgsCollect } from "./useVgsCollect";
import { fetchVaultCredentials } from "@/networking/vault.api";
import { VgsCollectSubmitResponse } from "@/networking/vault.api";
import { usesVgsCollect } from "@/lib/vgs-utils";
import { AvailablePaymentMethod } from "@/networking/carts.api";

interface UseVgsPaymentParams {
  cartToken: string;
  selectedPaymentType: string;
  paymentOptions: AvailablePaymentMethod[];
  onSuccess: (response: VgsCollectSubmitResponse) => void;
  onError: (error: string) => void;
  enabled?: boolean;
}

export function useVgsPayment({
  cartToken,
  selectedPaymentType,
  paymentOptions,
  onSuccess,
  onError,
  enabled = true,
}: UseVgsPaymentParams) {
  // Initialize the VGS collect hook
  const vgs = useVgsCollect({ onSuccess, onError });

  const selectedOption = paymentOptions.find(
    (option) => option.type === selectedPaymentType,
  );

  const shouldUseVgs = usesVgsCollect(selectedOption);
  const paymentAccountId = selectedOption?.id;

  useEffect(() => {
    if (!enabled || !shouldUseVgs || !paymentAccountId) {
      return;
    }

    const initializeVgs = async () => {
      try {
        const credentials = await fetchVaultCredentials(cartToken);

        // Pass credentials directly to the hook
        await vgs.initializeForm(credentials);
      } catch (error) {
        console.error("🔴 useVgsPayment initialization error:", error);
        onError("Failed to initialize payment form");
      }
    };

    initializeVgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    shouldUseVgs,
    cartToken,
    paymentAccountId,
    vgs.initializeForm,
    vgs.sessionRevision,
    onError,
  ]);

  return {
    ...vgs,
    iframeLoadError: shouldUseVgs ? vgs.iframeLoadError : false,
    paymentAccountId,
  };
}
